import { NavigationExtras } from '@angular/router';
import { Observable, of } from 'rxjs';
import { UserContextModel } from '../auth/user-context.model';
import { Store } from '@ngxs/store';

/**
 * Represents the application context object passed to the help-center-widget.
 * Contains information about the user's current session state for support purposes.
 */
export class HelpCenterAppContext {
	/** Current route or page URL */
	location: string;
	/** Last action performed by the user (e.g. 'navigate', 'click', 'submit') */
	action: string | object;
	/** Type of object the user is interacting with (e.g. 'order', 'queue', 'profile') */
	objectType: string | object;
	/** Username or user identifier */
	user: string | UserContextModel;
	/** ISO timestamp of when the context was captured */
	timestamp: string;
	/** Last error encountered, if any */
	lastError: any;

	constructor(params: any) {
		if (params && typeof params === 'object') {
			Object.assign(this, params);
		}
	}
}

export class PatchContextModel extends HelpCenterAppContext {
	closePanel?: boolean;
	constructor() {
		super({});
		this.closePanel = false;
	}
}

export class AppHeaderData {
	public logo: string;
	public module: string;
}

export class AppSwitcherMenu {
	public options: Array<AppSwitcherMenuOptions>;
	public appDropdown: AppSwitcherMenuDropdown;
}

export class AppSwitcherMenuOptions {
	public type: string;
	public label?: string;
	public image?: string;
}

export class AppSwitcherMenuDropdown {
	public label: string;
	public isImage: boolean;
}

export class RoleByPermission {
	public return: boolean;
	public role: string;
	public path?: string;
}

export class RedirectionData {
	public path: string;
	public isAdmin: boolean;
}

export class ChangeRoutOnErrorData {
	public changeRoute: boolean;
	public path: string;
	public routerOptions: NavigationExtras;
}

export class HttpInterceptorExceptions {
	toaster?: Array<any>;
	handledByOtherInterceptor?: Array<any>;
	swallowValidErrors?: Array<any>;
}

export class FormSchemaGroups {
	SCHEMA_CONFIG: any;
	SIMPLIFIED_NAV_CONFIG_SETS: {
		schemas: Set<string>;
		forms: Set<string>;
	};
	constructor() {
		this.SCHEMA_CONFIG = {};
		this.SIMPLIFIED_NAV_CONFIG_SETS = {
			schemas: new Set(),
			forms: new Set()
		}
	}
}

export abstract class AppServiceModel {
	httpInterceptorExceptions: HttpInterceptorExceptions;
	signalRConnectionIdApps: Array<any>;
	headerIgnoreFormTypeApps: Array<any>;

	/**
	 * Gets the form schema groups to be shared across libraries
	 */
	getFormSchemaGroups(): FormSchemaGroups {
		return new FormSchemaGroups();
	};

	/**
	 * Given the user permissions, generate the App header & module
	 * @param permissions
	 */
	abstract getAppHeader(permissions: any): AppHeaderData;

	/**
	 * Given the user permissions, generate the AppSwitcher
	 *
	 * @param user
	 * @returns
	 */
	abstract getAppSwitcher(user: any, currentModule: string): AppSwitcherMenu;

	/**
	 * Given the module path, check if it can show the return to inbox.
	 *
	 * @param module
	 * @returns
	 */
	abstract getHeaderShowInboxButton(module: string): boolean;

	/**
	 * Given the user permissions, check if it can show the appSwitcher
	 *
	 * @param permissions
	 * @returns
	 */
	abstract getCanAccessMultipleApps(permissions: any): boolean;

	/**
	 * Get the ROLE given the permissions array
	 *
	 * @param permissions
	 */
	abstract getRoleByPermission(permissions: any): RoleByPermission;

	/**
	 * Get the ROLE given the permissions array & current url
	 *
	 * @param permissions
	 * @param url
	 */
	abstract getRoleByPermissionAndUrl(permissions: any, url: string): RoleByPermission;

	/**
	 * Get the defautl PATH given the permissions
	 *
	 * @param permissions
	 */
	abstract getDefaultPathByPermissions(permissions: any): string;

	/**
	 * Get the Default PATH given the url and the Module
	 * @param url
	 * @param currentModule
	 */
	abstract getDefaultPath(url: string, currentModule: string): any;

	/**
	 * Get the user data and menu from an endpoint or JWT
	 *
	 * @param userInfo The payload from the JWT decode
	 * @param token The JWT token
	 */
	abstract getUserData(userInfo: any, token: string): Observable<UserContextModel>;

	/**
	 * Update the user data using the token
	 * Default functionality added to avoid breaks in Apss that doesn't use it
	 *
	 * @param userInfo The payload from the JWT decode
	 * @param token The JWT token
	 */
	getUserDataFromToken(userInfo: any, token: string): Observable<UserContextModel> {
		const user = new UserContextModel();
		const {
			sub,
			first_name,
			last_name,
			email,
			permission,
			role,
			'cv.connection': cvConnection,
			name,
			orgName,
			orgType,
			orgId,
			userType,
		} = userInfo;

		user.id = sub;
		user.cvConnection = cvConnection;
		user.firstName = first_name;
		user.lastName = last_name;
		user.name = `${first_name} ${last_name}`;
		user.email = email;
		user.userName = name;
		user.userType = userType;
		user.organizationName = orgName;
		user.organizationId = orgId;
		user.organizationType = orgType;
		user.permission = permission;
		user.role = role;
		user.token = token;

		return of(user);
	};

	/**
	 * Get redirectionData given the url and role
	 *
	 * @param url
	 * @param role
	 * @returns
	 */
	abstract getRedirectionData(url: string, role: any): RedirectionData;

	/**
	 * Skip CanActivate from auth.guard
	 *
	 * @param currentRoute
	 * @param role
	 * @returns TRUE|FALSE
	 */
	abstract getSkipCanActivateGuard(currentRoute: any, role: any): boolean;

	/**
	 * Get the module main url to use as base link for the notifications
	 *
	 * @param modulePath
	 * @returns
	 */
	abstract getMainModuleUrl(modulePath: string): string;

	/**
	 * Get the appName to use for the telemetry connection
	 *
	 * @param url
	 */
	abstract getTelemetryAppName(url: string): string;

	/**
	 * Verify if its needed a route change on error
	 *
	 * @param module
	 * @param errorMessage
	 * @returns
	 */
	abstract getChangeRouteOnError(module: string, errorMessage: string): ChangeRoutOnErrorData;

	/**
	 * Get the signalR connection url given the user role
	 *
	 * @param module
	 * @param user
	 * @param config
	 * @returns
	 */
	abstract getSignalRUrl(module: string, url: string, user: any, config: any): string;

	/**
	 * Returns false or true whether a list of module are valid for sinalR connection
	 * @param modules Array of allowed signalRmodules
	 * @param currentModule current moduel
	 * @return boolean
	 */
	abstract allowedSignalRModule(modules: Array<string>, currentModule: string): boolean;

	/**
	 * Function to check if the signalR event can be stored in the state.
	 * OPTIONAL IMPLEMENTATION:
	 * If the app requires special logic (invisionUX), you have to implement it in the perApp service.
	 * If not, the default value returned it is true;
	 *
	 * @param data An Object with the function's data required to process
	 * @returns boolean
	 */
	canSignalRStoreEvents(data: any): boolean {
		return true;
	}

	abstract getSignalExceptionEvents(): any[];

	/**
	 * Get the default state to update while moving between apps
	 *
	 * @param module
	 */
	abstract getModuleBaseState(module: string): any;

	abstract isSMSEnabled(store: Store): boolean;

	/**
	 * Get the Main API Configuration where the utils exists that can be used to load external configuration
	 */
	abstract getSecretConfigAPI(): string;

	/**
	 * Get the Main API Secret Config Route
	 */
	abstract getSecretConfigRoute(): string;

	abstract getStorageAppId(): string;

	/**
	 * Builds the HelpCenterAppContext object with the current session state.
	 * Each app can customize how the context is constructed.
	 *
	 * @param {Partial<HelpCenterAppContext>} data - Partial context data to merge.
	 * @returns {HelpCenterAppContext} The complete app context object for the help-center-widget.
	 */
	buildHelpCenterContext(data: Partial<HelpCenterAppContext>): HelpCenterAppContext {
		return {
			location: data.location || '',
			action: data.action || '',
			objectType: data.objectType || '',
			user: data.user || '',
			timestamp: data.timestamp || new Date().toISOString(),
			lastError: data.lastError || null
		};
	};
}
